// Door-window resolution lives in @produce/shared so web and mobile
// stay in sync. This file re-exports the shared helpers and adds
// diffDayDoors, the web-only producer save-flow helper that turns
// the form's local festivalDays state into a briefing_day_doors
// upsert/delete diff.
export {
  doorsForDay,
  reconstructFestivalDays,
  resolveAnalyticsWindow,
  formatDoorsForContext,
  type DayDoors,
  type DoorsWindow,
} from "@produce/shared";

import type { DayDoors } from "@produce/shared";

export type DayDoorsUpsert = {
  briefing_id: string;
  event_date: string;
  doors_open: string | null;
  doors_close: string | null;
};

/**
 * One row of the producer's Festival Days form: the date plus the
 * "HH:MM" strings from the two <input type="time"> fields. Blank means
 * "not set" and is stored as NULL.
 */
export interface LocalFestivalDay {
  date: string;
  startTime: string | null;
  endTime: string | null;
}

/** Blank / whitespace-only form input means "not set". */
const formTime = (t: string | null | undefined): string | null => (t && t.trim() !== "" ? t : null);

/** Stored times come back as "HH:MM:SS"; the form speaks "HH:MM". */
const storedTime = (t: string | null | undefined): string | null =>
  t && t.length >= 5 ? t.substring(0, 5) : null;

/**
 * `desired` — every row the producer's days imply, whether or not it
 * needs writing. It is the table's contents once the write lands, so
 * callers that seed a cache use this rather than `toUpsert`.
 * `toUpsert` — the subset of `desired` that differs from what is stored.
 * `toDelete` — stored dates the producer removed from the festival.
 */
export function diffDayDoors(input: {
  briefingId: string;
  localDays: LocalFestivalDay[];
  existing: DayDoors[];
}): { desired: DayDoorsUpsert[]; toUpsert: DayDoorsUpsert[]; toDelete: string[] } {
  const { briefingId, localDays, existing } = input;

  // One row per date, last card wins. Nothing stops the producer from
  // typing the same date into two Festival Days cards, and
  // briefing_day_doors is unique on (briefing_id, event_date) — two
  // rows with the same conflict target abort the whole upsert with
  // Postgres 21000 ("cannot affect row a second time"), which on a new
  // production would strand the producer on step 0.
  const byDate = new Map(localDays.map((d) => [d.date, d]));
  const uniqueDays = [...byDate.values()];

  const localDates = new Set(byDate.keys());
  const storedByDate = new Map(existing.map((row) => [row.event_date, row]));

  // Only days whose window actually moved are written. Rewriting every
  // day on every save is the unchanged-row UPDATE pattern that drove the
  // producer editor's database CPU peaks (see saveScope.ts) — small here
  // (one row per festival day), but the baseline is already in hand, so
  // there is no reason to pay it.
  const desired: DayDoorsUpsert[] = uniqueDays.map((d) => ({
    briefing_id: briefingId,
    event_date: d.date,
    doors_open: formTime(d.startTime),
    doors_close: formTime(d.endTime),
  }));

  const toUpsert = desired.filter((row) => {
    const stored = storedByDate.get(row.event_date);
    if (!stored) return true;
    return (
      storedTime(stored.doors_open) !== row.doors_open ||
      storedTime(stored.doors_close) !== row.doors_close
    );
  });

  const toDelete = existing
    .filter((row) => !localDates.has(row.event_date))
    .map((row) => row.event_date);

  return { desired, toUpsert, toDelete };
}
